/**
 * ICS Parser Utility
 * Parses ICS calendar feeds and extracts event information
 */
class ICSParser {
  constructor() {
    this.dateRegex = /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?/;
    this.dateOnlyRegex = /(\d{4})(\d{2})(\d{2})/;
  }

  /**
   * Parse ICS content and return structured event data
   * @param {string} icsContent - Raw ICS file content
   * @param {number} maxEvents - Maximum number of events to return
   * @param {number} daysAhead - How many days ahead to look for events
   * @returns {Array} Array of event objects
   */
  parse(icsContent, maxEvents = 10, daysAhead = 365) {
    if (!icsContent || typeof icsContent !== 'string') {
      console.warn('Invalid ICS content provided');
      return [];
    }

    const events = [];
    const lines = icsContent.split(/\r?\n/);
    let currentEvent = null;
    let inEvent = false;

    const now = new Date();
    const cutoffDate = new Date(now.getTime() + (daysAhead * 24 * 60 * 60 * 1000));

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === 'BEGIN:VEVENT') {
        inEvent = true;
        currentEvent = {
          summary: '',
          description: '',
          dtstart: null,
          dtend: null,
          location: '',
          url: '',
          uid: ''
        };
        continue;
      }

      if (line === 'END:VEVENT' && inEvent) {
        if (currentEvent && this.isValidEvent(currentEvent, now, cutoffDate)) {
          events.push(this.formatEvent(currentEvent));
        }
        inEvent = false;
        currentEvent = null;
        continue;
      }

      if (!inEvent || !currentEvent) continue;

      // Handle multi-line properties (continuation lines start with space or tab)
      let fullLine = line;
      while (i + 1 < lines.length && /^[\s\t]/.test(lines[i + 1])) {
        i++;
        fullLine += lines[i].trim();
      }

      this.parseEventProperty(fullLine, currentEvent);
    }

    // Sort by start date and limit results
    return events
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      .slice(0, maxEvents);
  }

  /**
   * Parse individual event properties
   * @param {string} line - Property line from ICS
   * @param {object} event - Current event object
   */
  parseEventProperty(line, event) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return;

    const property = line.substring(0, colonIndex);
    const value = line.substring(colonIndex + 1);

    // Handle property parameters (e.g., DTSTART;TZID=America/New_York:...)
    const [propertyName] = property.split(';');

    switch (propertyName.toUpperCase()) {
      case 'SUMMARY':
        event.summary = this.unescapeText(value);
        break;
      case 'DESCRIPTION':
        event.description = this.unescapeText(value);
        break;
      case 'DTSTART':
        event.dtstart = this.parseDate(value);
        break;
      case 'DTEND':
        event.dtend = this.parseDate(value);
        break;
      case 'LOCATION':
        event.location = this.unescapeText(value);
        break;
      case 'URL':
        event.url = value;
        break;
      case 'UID':
        event.uid = value;
        break;
    }
  }

  /**
   * Parse ICS date format to JavaScript Date
   * @param {string} dateString - ICS formatted date
   * @returns {Date|null} Parsed date or null if invalid
   */
  parseDate(dateString) {
    if (!dateString) return null;

    // Handle date-time format: YYYYMMDDTHHMMSSZ
    let match = dateString.match(this.dateRegex);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return new Date(Date.UTC(
        parseInt(year),
        parseInt(month) - 1, // JavaScript months are 0-indexed
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second)
      ));
    }

    // Handle date-only format: YYYYMMDD
    match = dateString.match(this.dateOnlyRegex);
    if (match) {
      const [, year, month, day] = match;
      return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
    }

    console.warn('Could not parse date:', dateString);
    return null;
  }

  /**
   * Unescape ICS text content
   * @param {string} text - Escaped ICS text
   * @returns {string} Unescaped text
   */
  unescapeText(text) {
    if (!text) return '';

    return text
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\;/g, ';')
      .replace(/\\,/g, ',')
      .replace(/\\\\/g, '\\');
  }

  /**
   * Check if event is valid and within date range
   * @param {object} event - Event object
   * @param {Date} now - Current date
   * @param {Date} cutoffDate - Maximum date to include
   * @returns {boolean} Whether event is valid
   */
  isValidEvent(event, now, cutoffDate) {
    if (!event.dtstart || !event.summary) {
      return false;
    }

    const startDate = new Date(event.dtstart);

    // Skip past events (events that ended before now)
    const endDate = event.dtend ? new Date(event.dtend) : startDate;
    if (endDate < now) {
      return false;
    }

    // Skip events beyond cutoff date
    if (startDate > cutoffDate) {
      return false;
    }

    return true;
  }

  /**
   * Format event for frontend consumption
   * @param {object} rawEvent - Raw parsed event
   * @returns {object} Formatted event object
   */
  formatEvent(rawEvent) {
    const startDate = new Date(rawEvent.dtstart);
    const endDate = rawEvent.dtend ? new Date(rawEvent.dtend) : null;

    return {
      id: rawEvent.uid || this.generateEventId(rawEvent),
      title: rawEvent.summary || 'Untitled Event',
      description: rawEvent.description || '',
      startDate: startDate.toISOString(),
      endDate: endDate ? endDate.toISOString() : null,
      location: rawEvent.location || '',
      url: rawEvent.url || '',
      isAllDay: this.isAllDayEvent(rawEvent.dtstart, rawEvent.dtend),
      formattedDate: this.formatDisplayDate(startDate, endDate),
      formattedTime: this.formatDisplayTime(startDate, endDate)
    };
  }

  /**
   * Check if event is all-day
   * @param {Date} start - Start date
   * @param {Date} end - End date
   * @returns {boolean} Whether event is all-day
   */
  isAllDayEvent(start, end) {
    if (!start) return false;

    const startDate = new Date(start);
    return startDate.getUTCHours() === 0 &&
           startDate.getUTCMinutes() === 0 &&
           startDate.getUTCSeconds() === 0;
  }

  /**
   * Format date for display
   * @param {Date} start - Start date
   * @param {Date} end - End date
   * @returns {string} Formatted date string
   */
  formatDisplayDate(start, end) {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    const startStr = start.toLocaleDateString(undefined, options);

    if (!end || start.toDateString() === end.toDateString()) {
      return startStr;
    }

    return `${startStr} - ${end.toLocaleDateString(undefined, options)}`;
  }

  /**
   * Format time for display
   * @param {Date} start - Start date
   * @param {Date} end - End date
   * @returns {string} Formatted time string
   */
  formatDisplayTime(start, end) {
    if (this.isAllDayEvent(start, end)) {
      return 'All day';
    }

    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    const startStr = start.toLocaleTimeString(undefined, timeOptions);

    if (!end) {
      return startStr;
    }

    const endStr = end.toLocaleTimeString(undefined, timeOptions);
    return `${startStr} - ${endStr}`;
  }

  /**
   * Generate a unique ID for events without UID
   * @param {object} event - Event object
   * @returns {string} Generated ID
   */
  generateEventId(event) {
    const str = `${event.summary}-${event.dtstart}-${event.location}`;
    return btoa(str).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  }
}

// Export for use in other scripts
window.ICSParser = ICSParser;